<template>
	<section class="insicht insicht--fade-up">
		<div class="vouchers__container">
			<GeneralMessage
				ref="initialError"
				v-if="initialError"
				:message="initialError"
				type="error"
			/>
			<div
				class="vouchers__content"
				v-if="content.text"
				v-html="content.text"
			/>
			<Button
				v-if="content.videoUrl"
				type="button"
				level="tertiary"
				:text="$t('vouchers.tutorialButton')"
				arrow
				@click.prevent="onVideoClick(content.videoUrl)"
			/>
		</div>
		<div class="vouchers__container">
			<ul class="vouchers__list">
				<li
					class="vouchers__item"
					v-for="(voucher, index) in vouchers"
					:key="index"
				>
					<img
						class="vouchers__item-image"
						:src="voucher.image"
						:alt="voucher.title"
						loading="lazy"
					>
					<span
						class="vouchers__item-title"
						v-html="voucher.title"
					/>
					<p
						class="vouchers__item-text"
						v-html="voucher.text"
					/>
					<div class="vouchers__item-price">
						<span
							class="vouchers__item-price-value"
							v-if="!voucher.custom"
							v-html="`${$options.filters.fwgpricecent(voucher.price)} / ${$t('vouchers.piece')}`"
						/>
						<template v-else>
							<span
								class="vouchers__item-price-prefix"
								v-html="'CHF'"
							/>
							<input
								v-model.number="customValues[voucher.variantCode]"
								type="number"
								min="0"
								step="1"
								class="vouchers__item-price-input"
								@input="onInput(voucher)"
							>
							<span
								class="vouchers__item-price-suffix"
								v-html="`/ ${$t('vouchers.piece')}`"
							/>
						</template>
					</div>
					<QuantityCounter
						:quantity="quantities[voucher.variantCode]"
						base-class="vouchers__item-quantity"
						@sub="onSub(voucher)"
						@add="onAdd(voucher)"
						:locked="voucher.custom ? !customValues[voucher.variantCode] : !voucher.price"
					/>
				</li>
			</ul>
		</div>

		<div
			class="vouchers__sum"
			v-if="showSum"
		>
			<div
				class="vouchers__sum-item"
				v-for="(voucher, index) in activeVouchers"
				:key="index"
			>
				<span
					class="vouchers__sum-item-title"
					v-html="voucher.title"
				/>
				<span
					class="vouchers__sum-item-quantity"
					v-html="voucher.quantity"
				/>
				<span
					class="vouchers__sum-item-price"
					v-html="$options.filters.fwgpricecent(voucher.quantity * voucher.price)"
				/>
			</div>
			<div class="vouchers__sum-total">
				<span
					class="vouchers__sum-total-title"
					v-html="$t('vouchers.total')"
				/>
				<span
					class="vouchers__sum-total-price"
					v-html="$options.filters.fwgpricecent(total)"
				/>
			</div>
		</div>

		<div
			class="vouchers__sum"
			v-else
		>
			<div
				class="vouchers__sum-item"
			>
				<span
					class="vouchers__sum-item-title"
					v-html="$t('vouchers.noVouchersSelected')"
				/>
			</div>
		</div>

		<div
			class="vouchers__sticky"
			v-if="showSum"
		>
			<div class="vouchers__sticky-content">
				<div class="vouchers__sticky-count">
					<div
						class="vouchers__sticky-count-label"
						v-html="$t('vouchers.vouchers')"
					/>
					<div
						class="vouchers__sticky-count-value"
						v-html="totalCount"
					/>
				</div>

				<div class="vouchers__sticky-total">
					<div
						class="vouchers__sticky-total-label"
						v-html="$t('vouchers.total')"
					/>
					<div
						class="vouchers__sticky-total-value"
						v-html="$options.filters.fwgpricecent(total)"
					/>
				</div>
			</div>
			<Button
					type="button"
					level="primary"
					:text="$t('vouchers.orderButton')"
					@click="scrollToForm"
				/>
		</div>

		<div
			class="vouchers__form"
			v-if="showSum"
		>
			<GeneralMessage
				ref="message"
				v-if="response.message"
				:message="response.message"
				:type="response.type"
				stick
			/>

			<ValidationObserver
				ref="form"
				v-slot="{ invalid, errors }"
				tag="form"
				class="checkout-form__form-wrapper vouchers__form-wrapper"
				novalidate
				@submit.prevent="onSubmit"
			>

				<FormInputCheckbox
					name="print"
					value="printAtHome"
					v-model="form.printAtHome"
					immediate
				>
					<span v-html="$t('vouchers.printAtHomeLabel')"/>
				</FormInputCheckbox>
				<span class="additional-info" v-html="$t('vouchers.printAtHomeHint')"/>

				<h2
					class="vouchers__form-title"
					v-html="$t('vouchers.contactTitle')"
				/>

				<div class="input-row">
					<ValidationProvider
						class="input-row__wrapper"
						name="email"
						rules="required|email"
						v-slot="{ errors, invalid, validated }"
						tag="div"
						:customMessages="{ required: $t('forms.errors.fieldEmpty'), email: $t('forms.errors.emailInvalid') }"
					>
						<FormInput
							type="email"
							name="email"
							autocomplete="email"
							:placeholder="$t('forms.labels.email')"
							:label="$t('forms.labels.email')"
							v-model="form.email"
							:error="errors.length > 0"
							required
						/>
						<span
							class="input-error"
							v-if="invalid && validated"
							v-html="errors[0]"
						/>
						<span class="additional-info" v-html="$t('vouchers.emailHint')"/>
					</ValidationProvider>

					<ValidationProvider
						class="input-row__wrapper"
						name="shippingPhone"
						rules="required|phone"
						v-slot="{ errors, invalid, validated }"
						tag="div"
						:customMessages="{ required: $t('forms.errors.fieldEmpty'), phone: $t('forms.errors.phone') }"
					>
						<FormInput
							type="tel"
							:placeholder="$t('forms.labels.phone')"
							:label="$t('forms.labels.phone')"
							name="phone"
							v-model="phoneNumber"
							:error="errors.length > 0"
							required
						/>
						<span
							class="input-error"
							v-if="invalid && validated"
							v-html="errors[0]"
						/>
						<span class="additional-info" v-html="$t('vouchers.phoneHint')"/>
					</ValidationProvider>
				</div>

				<FormInputCheckbox
					name="gift"
					value="sendAsGift"
					v-model="form.sendAsGift"
					immediate
				>
					<span v-html="$t('vouchers.asGiftLabel')"/>
				</FormInputCheckbox>
				<!-- <span class="additional-info" v-html="$t('vouchers.printAtHomeHint')"/> -->

				<template v-if="form.sendAsGift && form.printAtHome">
					<h2
						class="vouchers__form-title"
						v-html="$t('vouchers.giftInformation')"
					/>
					<div class="input-row">
						<ValidationProvider
							class="input-row__wrapper"
							name="giftSender"
							v-slot="{ errors, invalid, validated }"
							tag="div"
						>
							<FormInput
								type="text"
								:placeholder="$t('vouchers.giftSender')"
								:label="$t('vouchers.giftSender')"
								name="giftSender"
								v-model="form.giftSender"
								:error="errors.length > 0"
							/>
							<span
								class="input-error"
								v-if="invalid && validated"
								v-html="errors[0]"
							/>
						</ValidationProvider>

						<ValidationProvider
							class="input-row__wrapper"
							name="giftReceiver"
							v-slot="{ errors, invalid, validated }"
							tag="div"
						>
							<FormInput
								type="text"
								:placeholder="$t('vouchers.giftReceiver')"
								:label="$t('vouchers.giftReceiver')"
								name="giftReceiver"
								v-model="form.giftReceiver"
								:error="errors.length > 0"
							/>
							<span
								class="input-error"
								v-if="invalid && validated"
								v-html="errors[0]"
							/>
						</ValidationProvider>
					</div>
					<div
						v-for="i in [1, 2, 3]"
						:key="i"
						class="input-row"
					>
						<ValidationProvider
							class="input-row__wrapper"
							:name="`giftMessage${i}`"
							v-slot="{ errors, invalid, validated }"
							tag="div"
						>
							<FormInput
								:placeholder="$t(`vouchers.giftMessage${i}`)"
								:label="$t(`vouchers.giftMessage${i}`)"
								:name="`giftMessage${i}`"
								v-model="form[`giftMessage${i}`]"
								:maxlength="55"
								:required="false"
								:error="errors.length > 0"
								show-charsleft
							/>
						</ValidationProvider>
					</div>
				</template>

				<template v-if="form.sendAsGift || form.printAtHome">
					<h2
						class="vouchers__form-title"
						v-html="$t('vouchers.billingAddressTitle')"
					/>
					<ValidationObserver
						tag="section"
						ref="billingAddress"
						vid="billingAddress"
						v-slot="{ invalid }"
					>

						<div class="input-row">
							<ValidationProvider
								class="input-row__wrapper"
								name="billingFirstname"
								rules="required"
								v-slot="{ errors, invalid, validated }"
								tag="div"
								:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
							>
								<FormInput
									type="text"
									:placeholder="$t('forms.labels.firstname')"
									:label="$t('forms.labels.firstname')"
									name="firstname"
									v-model="form.billingAddress.firstName"
									:error="errors.length > 0"
									required
								/>
								<span
									class="input-error"
									v-if="invalid && validated"
									v-html="errors[0]"
								/>
							</ValidationProvider>

							<ValidationProvider
								class="input-row__wrapper"
								name="billingLastname"
								rules="required"
								v-slot="{ errors, invalid, validated }"
								tag="div"
								:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
							>
								<FormInput
									type="text"
									:placeholder="$t('forms.labels.lastname')"
									:label="$t('forms.labels.lastname')"
									name="lastname"
									v-model="form.billingAddress.lastName"
									:error="errors.length > 0"
									required
								/>
								<span
									class="input-error"
									v-if="invalid && validated"
									v-html="errors[0]"
								/>
							</ValidationProvider>
						</div>

						<div class="input-row">
							<FormInput
								type="text"
								:placeholder="$t('forms.labels.company')"
								:label="$t('forms.labels.company')"
								:labelOption="$t('forms.labels.optional')"
								name="company"
								v-model="form.billingAddress.company"
								:required="false"
							/>
						</div>

						<div class="input-row">
							<ValidationObserver
								class="input-row__wrapper"
								tag="div"
								ref="billingStreetNr"
								v-slot="{ validated, invalid, errors }"
							>
								<div class="input-group">
									<ValidationProvider
										class="input-group__span-9"
										name="billingStreet"
										rules="required"
										v-slot="{ errors, invalid }"
										tag="div"
										:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
									>
										<FormInput
											type="text"
											:placeholder="$t('forms.labels.street')"
											:label="$t('forms.labels.street')"
											name="street"
											v-model="form.billingAddress.street"
											:error="errors.length > 0"
											required
										/>
									</ValidationProvider>
									<ValidationProvider
											class="input-group__span-3"
											name="billingStreetNumber"
											rules="required"
											v-slot="{ errors, invalid }"
											tag="div"
											:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
										>
										<FormInput
											type="text"
											:placeholder="$t('forms.labels.number')"
											:label="$t('forms.labels.number')"
											name="number"
											v-model="form.billingAddress.streetNumber"
											:error="errors.length > 0"
											required
										/>
									</ValidationProvider>
								</div>
								<div
									class="input-error input-group__span-12"
									v-if="invalid && validated"
									v-html="Object.values(errors).filter(value => !!value.join('').trim()).join(' ')"
								/>
							</ValidationObserver>

							<ValidationObserver
								class="input-row__wrapper"
								tag="div"
								ref="billingZipCity"
								v-slot="{ dirty, validated, invalid, errors }"
							>
								<div class="input-group">
									<ValidationProvider
										class="input-group__span-4 input-group__span-xxl-3"
										name="billingZip"
										rules="required|digits:4"
										v-slot="{ errors, invalid }"
										tag="div"
										:customMessages="{ required: $t('forms.errors.fieldEmpty'), digits: $t('forms.errors.zip') }"
									>
										<FormInput
											ref="zipInputBilling"
											type="text"
											:placeholder="$t('forms.labels.zip')"
											:label="$t('forms.labels.zip')"
											@keydown="onZipKeydown"
											@input="onZipInputBilling"
											name="zip"
											v-model="form.billingAddress.postcode"
											:minlength="4"
											:maxlength="4"
											inputmode="decimal"
											pattern="[0-9]{4}"
											:error="errors.length > 0"
											required
										/>
									</ValidationProvider>
									<ValidationProvider
											class="input-group__span-8 input-group__span-xxl-9"
											name="billingCity"
											rules="required"
											v-slot="{ errors, invalid }"
											tag="div"
											:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
										>
										<FormInput
											type="text"
											:placeholder="$t('forms.labels.city')"
											:label="$t('forms.labels.city')"
											name="city"
											v-model="form.billingAddress.city"
											:error="errors.length > 0"
											required
										/>
									</ValidationProvider>
								</div>
								<div
									class="input-error input-group__span-12"
									v-if="invalid && (validated || dirty)"
									v-html="Object.values(errors).filter(value => !!value.join('').trim()).join(' ')"
								/>
							</ValidationObserver>
						</div>

						<div class="input-row">
							<FormInputSelect
								:options="countryOptions"
								v-model="form.billingAddress.countryCode"
								:label="$t('forms.labels.country')"
								name="country"
							/>
						</div>

					</ValidationObserver>

				</template>

				<template v-if="!form.printAtHome">
					<h2
						class="vouchers__form-title"
						v-html="$t('vouchers.addressTitle')"
					/>

					<ValidationObserver
						tag="section"
						ref="shippingAddress"
						vid="shippingAddress"
						v-slot="{ invalid }"
					>

						<div class="input-row">
							<ValidationProvider
								class="input-row__wrapper"
								name="shippingFirstname"
								rules="required"
								v-slot="{ errors, invalid, validated }"
								tag="div"
								:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
							>
								<FormInput
									type="text"
									:placeholder="$t('forms.labels.firstname')"
									:label="$t('forms.labels.firstname')"
									name="firstname"
									v-model="form.shippingAddress.firstName"
									:error="errors.length > 0"
									required
								/>
								<span
									class="input-error"
									v-if="invalid && validated"
									v-html="errors[0]"
								/>
							</ValidationProvider>
							<ValidationProvider
								class="input-row__wrapper"
								name="shippingLastname"
								rules="required"
								v-slot="{ errors, invalid, validated }"
								tag="div"
								:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
							>
								<FormInput
									type="text"
									:placeholder="$t('forms.labels.lastname')"
									:label="$t('forms.labels.lastname')"
									name="lastname"
									v-model="form.shippingAddress.lastName"
									:error="errors.length > 0"
									required
								/>
								<span
									class="input-error"
									v-if="invalid && validated"
									v-html="errors[0]"
								/>
							</ValidationProvider>
						</div>

						<div class="input-row">
							<FormInput
								type="text"
								:placeholder="$t('forms.labels.company')"
								:label="$t('forms.labels.company')"
								:labelOption="$t('forms.labels.optional')"
								name="company"
								v-model="form.shippingAddress.company"
								:required="false"
							/>
						</div>

						<div class="input-row">
							<ValidationObserver
								class="input-row__wrapper"
								tag="div"
								ref="shippingStreetNr"
								v-slot="{ validated, invalid, errors }"
							>
								<div class="input-group">
									<ValidationProvider
										class="input-group__span-9"
										name="shippingStreet"
										rules="required"
										v-slot="{ errors, invalid }"
										tag="div"
										:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
									>
										<FormInput
											type="text"
											:placeholder="$t('forms.labels.street')"
											:label="$t('forms.labels.street')"
											name="street"
											v-model="form.shippingAddress.street"
											:error="errors.length > 0"
											required
										/>
									</ValidationProvider>
									<ValidationProvider
											class="input-group__span-3"
											name="shippingStreetNumber"
											rules="required"
											v-slot="{ errors, invalid }"
											tag="div"
											:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
										>
										<FormInput
											type="text"
											:placeholder="$t('forms.labels.number')"
											:label="$t('forms.labels.number')"
											name="number"
											v-model="form.shippingAddress.streetNumber"
											:error="errors.length > 0"
											required
										/>
									</ValidationProvider>
								</div>
								<div
									class="input-error input-group__span-12"
									v-if="invalid && validated"
									v-html="Object.values(errors).filter(value => !!value.join('').trim()).join(' ')"
								/>
							</ValidationObserver>

							<ValidationObserver
								class="input-row__wrapper"
								tag="div"
								ref="shippingZipCity"
								v-slot="{ dirty, validated, invalid, errors }"
							>
								<div class="input-group">
									<ValidationProvider
										class="input-group__span-4 input-group__span-xxl-3"
										name="shippingZip"
										rules="required|digits:4"
										v-slot="{ errors, invalid }"
										tag="div"
										:customMessages="{ required: $t('forms.errors.fieldEmpty'), digits: $t('forms.errors.zip') }"
									>
										<FormInput
											ref="zipInput"
											type="text"
											:placeholder="$t('forms.labels.zip')"
											:label="$t('forms.labels.zip')"
											@keydown="onZipKeydown"
											@input="onZipInput"
											name="zip"
											v-model="form.shippingAddress.postcode"
											:minlength="4"
											:maxlength="4"
											inputmode="decimal"
											pattern="[0-9]{4}"
											:error="errors.length > 0"
											required
										/>
									</ValidationProvider>
									<ValidationProvider
											class="input-group__span-8 input-group__span-xxl-9"
											name="shippingCity"
											rules="required"
											v-slot="{ errors, invalid }"
											tag="div"
											:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
										>
										<FormInput
											type="text"
											:placeholder="$t('forms.labels.city')"
											:label="$t('forms.labels.city')"
											name="city"
											v-model="form.shippingAddress.city"
											:error="errors.length > 0"
											required
										/>
									</ValidationProvider>
								</div>
								<div
									class="input-error input-group__span-12"
									v-if="invalid && (validated || dirty)"
									v-html="Object.values(errors).filter(value => !!value.join('').trim()).join(' ')"
								/>
							</ValidationObserver>
						</div>

						<div class="input-row">
							<FormInputSelect
								:options="countryOptions"
								v-model="form.shippingAddress.countryCode"
								:label="$t('forms.labels.country')"
								name="country"
							/>
						</div>

					</ValidationObserver>
				</template>

				<div class="input-row">
					<FormInputTextarea
							:placeholder="$t('vouchers.notesLabel')"
							:label="$t('vouchers.notesLabel')"
							v-model="form.notes"
						/>
				</div>

				<Button
					type="submit"
					level="primary"
					:text="$t('vouchers.orderButton')"
					:disabled="formLocked"
				/>
			</ValidationObserver>
		</div>

		<Modal
			ref="videoModal"
		>
			<div
				class="overlay-video"
				v-html="videoEmbedCode"
			/>
		</Modal>
	</section>
</template>

<script>
import Vue from 'vue'
import { ValidationProvider, ValidationObserver } from "vee-validate"
import { mapGetters } from "vuex"
import scrrroll from 'scrrroll'
import FormInput from "@/components/FormInput"
import FormInputTextarea from "@/components/FormInputTextarea"
import FormInputCheckbox from "@/components/FormInputCheckbox"
import FormInputSelect from "@/components/FormInputSelectCustom"
import GenericMixin from '@/mixins/GenericVueMixin'
import QuantityCounter from '@/components/QuantityCounter';
import Button from '@/components/Button'
import GeneralMessage from '@/components/GeneralMessage'
import Modal from '@/components/Modal'
import videoEmbed from '@/utils/videoEmbed'

export default {
	mixins: [
		GenericMixin,
	],
	components: {
		ValidationProvider,
		ValidationObserver,
		FormInput,
		FormInputTextarea,
		FormInputCheckbox,
		FormInputSelect,
		Button,
		GeneralMessage,
		Modal,
		QuantityCounter
	},
	data() {
		return {
			initialError: this.$root.options.initialError,
			content: this.$root.options.content,
			config: this.$root.options.config,
			// vouchers: this.$root.options.vouchers.map(voucher => {
			// 	const price = parseFloat(voucher.price);

			// 	return {
			// 		...voucher,
			// 		custom: isNaN(price),
			// 		price: isNaN(price) ? 0 : price,
			// 		quantity: 0,
			// 	};
			// }),
			quantities: {},
			customValues: {},
			countryOptions: [
				{
					value: 'CH',
					label: this.$t('forms.countries.ch')
				}
			],
			form: {
				billingAddress: {
					firstName: "",
					lastName: "",
					countryCode: "CH",
					street: "",
					streetNumber: "",
					city: "",
					postcode: "",
					provinceName: "",
					company: "",
					phoneNumber: ""
				},
				shippingAddress: {
					firstName: "",
					lastName: "",
					countryCode: "CH",
					street: "",
					streetNumber: "",
					details: "",
					city: "",
					postcode: "",
					provinceName: "",
					company: "",
					phoneNumber: "",
				},
				email: '',
				notes: '',
				printAtHome: false,
				sendAsGift: false,
				giftSender: '',
				giftReceiver: '',
				giftMessage1: '',
				giftMessage2: '',
				giftMessage3: '',
			},
			response: {
				type: 'info',
				message: '',
			},
			formLocked: false,
			videoEmbedCode: '',
		}
	},
	computed: {
		...mapGetters('loader', [
			'isLoading',
		]),
		...mapGetters('vouchers', [
			'vouchers',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
		phoneNumber: {
			set(value) {
				this.form.shippingAddress.phoneNumber = value;
				this.form.billingAddress.phoneNumber = value;
			},
			get() {
				return this.form.shippingAddress.phoneNumber || this.form.billingAddress.phoneNumber;
			}
		},
		enhancedVouchers() {
			return this.vouchers.map(voucher => {
				return {
					...voucher,
					quantity: this.quantities[voucher.variantCode],
					price: voucher.custom ? this.customValues[voucher.variantCode] * 100 : voucher.price,
				}
			});
		},
		activeVouchers() {
			return this.enhancedVouchers.filter(voucher => voucher.quantity);
		},
		showSum() {
			return this.activeVouchers.length;
		},
		totalCount() {
			return this.enhancedVouchers.reduce((p, voucher) => {
				return p + voucher.quantity;
			}, 0);
		},
		total() {
			return this.enhancedVouchers.reduce((p, voucher) => {
				return p + (voucher.quantity * voucher.price);
			}, 0);
		}
	},
	methods: {
		onSub(voucher) {
			let quantity = 0;
			if (!voucher.custom || this.customValues[voucher.variantCode]) {
				quantity = Math.max(this.quantities[voucher.variantCode] - 1, 0);
			}
			Vue.set(this.quantities, voucher.variantCode, quantity);
		},
		onAdd(voucher) {
			let quantity = 0;
			if (!voucher.custom || this.customValues[voucher.variantCode]) {
				quantity = this.quantities[voucher.variantCode] + 1;
			}
			Vue.set(this.quantities, voucher.variantCode, quantity);
		},
		onInput(voucher) {
			let quantity = 0;
			if (this.customValues[voucher.variantCode]) {
				quantity = this.quantities[voucher.variantCode] || 1;
			}
			Vue.set(this.quantities, voucher.variantCode, quantity);
		},
		onZipKeydown(e) {
			if (!e.metaKey && !e.ctrlKey && e.key && e.key.length <= 1 && !/[0-9]/.test(e.key)) {
				e.preventDefault()
			}
		},
		onZipInput(value) {
			if (/[^\d]/.test(value)) {
				Vue.set(this.form.shippingAddress, 'postcode', value.replace(/[^\d]/ig,'').substring(0,4));
				this.$refs.zipInput.$refs.input.value = this.form.shippingAddress.postcode;
			}
		},
		onZipInputBilling(value) {
			if (/[^\d]/.test(value)) {
				Vue.set(this.form.billingAddress, 'postcode', value.replace(/[^\d]/ig,'').substring(0,4));
				this.$refs.zipInputBilling.$refs.input.value = this.form.billingAddress.postcode;
			}
		},
		onVideoClick(url) {
			this.videoEmbedCode = videoEmbed(url);
			this.$refs.videoModal.open();
		},
		scrollToForm() {
			if (this.$refs.form) {
				scrrroll.to(this.$refs.form.$el);
			}
		},
		async onSubmit(e) {
			e.preventDefault();
			this.loading = true;

			const valid = await this.$refs.form.validate();

			if (valid) {
				try {


					if (this.form.printAtHome) {
						Object.keys(this.form.shippingAddress).forEach(key => {
							Vue.set(this.form.shippingAddress, key, this.form.billingAddress[key]);
						});
					}
					else if (!this.form.sendAsGift) {
						Object.keys(this.form.billingAddress).forEach(key => {
							Vue.set(this.form.billingAddress, key, this.form.shippingAddress[key]);
						});
					}

					const response = await this.$store.dispatch('vouchers/orderVouchers', {
						...this.form,
						vouchers: this.activeVouchers.map(voucher => {
							return {
								variantCode: voucher.variantCode,
								quantity: voucher.quantity,
								price: voucher.price,
							}
						})
					});
				}
				catch(err) {
					if (err.response && err.response.data.message) {
						this.response.message = err.response.data.errors && Object.values(err.response.data.errors).length
							? Array.from(new Set(Object.values(err.response.data.errors).flat())).join()
							: err.response.data.message
					} else {
						this.response.message = this.$t('forms.errors.generic');
					}
					this.response.type = 'error';
					this.$nextTick(() => {
						this.$refs.message.scrollTo();
					});
					this.loading = false;
				}
			} else {
				await this.$nextTick();
				const inputError = this.$refs.form.$el.querySelector('.input-error');

				if (inputError) {
					scrrroll.to(inputError.parentNode)
				}
				this.loading = false
			}
		},
		resetForm() {
			this.$refs.form && this.$refs.form.reset();
			this.form.billingAddress.firstName = '';
			this.form.billingAddress.lastName = '';
			this.form.billingAddress.company = '';
			this.form.billingAddress.street = '';
			this.form.billingAddress.streetNumber = '';
			this.form.billingAddress.postcode = '';
			this.form.billingAddress.city = '';
			this.form.billingAddress.phoneNumber = '';
			this.form.shippingAddress.firstName = '';
			this.form.shippingAddress.lastName = '';
			this.form.shippingAddress.company = '';
			this.form.shippingAddress.street = '';
			this.form.shippingAddress.streetNumber = '';
			this.form.shippingAddress.postcode = '';
			this.form.shippingAddress.city = '';
			this.form.shippingAddress.phoneNumber = '';
			this.form.email = '';
			this.form.notes = '';
			this.form.printAtHome = false;
			this.form.sendAsGift = false;
			this.form.giftSender = '';
			this.form.giftReceiver = '';
			this.form.giftMessage1 = '';
			this.form.giftMessage2 = '';
			this.form.giftMessage3 = '';
			this.setupQuantities();
		},
		setupQuantities() {
			this.quantities = {};
			this.customValues = {};
			this.vouchers.forEach(voucher => {
				Vue.set(this.quantities, voucher.variantCode, 0);

				if (voucher.custom) {
					Vue.set(this.customValues, voucher.variantCode, 0);
				}
			});
		}
	},
	async created() {
		this.loading = true;
		try {
			await this.$store.dispatch('vouchers/loadVouchers');
			this.setupQuantities();
		}
		catch(err) {
			// blabla
		}
		this.loading = false;
	},
	mounted() {
		// this.loading = false;
	}
}
</script>

<style>

</style>
